import React, { useState, useEffect } from 'react';
import {
  Box,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Chip,
  Autocomplete,
  TextField,
  Typography,
  Paper,
  Divider
} from '@mui/material';
import {
  Public as PublicIcon,
  Lock as PrivateIcon,
  People as GroupIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import logger from '../utils/logger';

/**
 * Privacy Control Component
 *
 * Allows users to set privacy settings for content:
 * - Public: Visible to all family members
 * - Private: Visible to selected members or groups
 *
 * Props:
 * - familyCode: String - The family code
 * - value: Object - Current privacy settings { visibility: 'public'|'private', memberIds: [], groupTypes: [] }
 * - onChange: Function - Called when privacy settings change
 * - label: String - Label for the control (optional)
 */
const PrivacyControl = ({ familyCode, value = {}, onChange, label = 'Privacy Settings' }) => {
  const [visibility, setVisibility] = useState(value.visibility || 'public');
  const [selectedMembers, setSelectedMembers] = useState(value.memberIds || []);
  const [selectedGroups, setSelectedGroups] = useState(value.groupTypes || []);
  const [familyMembers, setFamilyMembers] = useState([]);
  const [familyGroups, setFamilyGroups] = useState([]);
  const [loading, setLoading] = useState(false);

  // Load family members and groups when component mounts or familyCode changes
  useEffect(() => {
    if (!familyCode) return;

    const loadFamilyData = async () => {
      setLoading(true);
      try {
        // Load family members from nested structure (families/{familyCode}/members)
        // NOTE: Querying the top-level 'users' collection without a familyCode
        // where-clause is blocked by Firestore security rules.
        const membersSnapshot = await getDocs(collection(db, 'families', familyCode, 'members'));
        const members = [];
        membersSnapshot.forEach((memberDoc) => {
          const memberData = memberDoc.data();
          members.push({
            id: memberDoc.id,
            name: memberData.displayName || `${memberData.firstName || ''} ${memberData.lastName || ''}`.trim() || memberData.email,
            email: memberData.email
          });
        });
        setFamilyMembers(members);
      } catch (error) {
        logger.error('Error loading family members:', error);
      }

      try {
        // Load family groups
        const groupsSnapshot = await getDocs(collection(db, 'families', familyCode, 'groups'));
        const groups = [];
        groupsSnapshot.forEach((groupDoc) => {
          const groupData = groupDoc.data();
          groups.push({
            type: groupDoc.id,
            name: groupData.name,
            description: groupData.description
          });
        });
        setFamilyGroups(groups);
      } catch (error) {
        logger.error('Error loading family groups:', error);
      }

      setLoading(false);
    };

    loadFamilyData();
  }, [familyCode]);

  // Update parent component when settings change
  useEffect(() => {
    if (onChange) {
      onChange({
        visibility,
        memberIds: selectedMembers,
        groupTypes: selectedGroups
      });
    }
  }, [visibility, selectedMembers, selectedGroups, onChange]);

  const handleVisibilityChange = (event) => {
    const newVisibility = event.target.value;
    setVisibility(newVisibility);

    // Clear selections when switching to public
    if (newVisibility === 'public') {
      setSelectedMembers([]);
      setSelectedGroups([]);
    }
  };

  return (
    <Paper sx={{ p: 2, bgcolor: 'background.default', border: '1px solid', borderColor: 'divider' }}>
      <FormControl component="fieldset" fullWidth>
        <FormLabel component="legend" sx={{ mb: 1, fontWeight: 600 }}>
          {label}
        </FormLabel>

        <RadioGroup value={visibility} onChange={handleVisibilityChange} sx={{ mb: 2 }}>
          <FormControlLabel
            value="public"
            control={<Radio />}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PublicIcon fontSize="small" color="action" />
                <Typography>Public - Visible to all family members</Typography>
              </Box>
            }
          />
          <FormControlLabel
            value="private"
            control={<Radio />}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PrivateIcon fontSize="small" color="action" />
                <Typography>Private - Visible to selected members or groups</Typography>
              </Box>
            }
          />
        </RadioGroup>

        {visibility === 'private' && (
          <Box sx={{ mt: 2 }}>
            <Divider sx={{ mb: 2 }} />

            {/* Select Members */}
            <Autocomplete
              multiple
              options={familyMembers}
              getOptionLabel={(option) => option.name}
              value={familyMembers.filter(m => selectedMembers.includes(m.id))}
              onChange={(event, newValue) => {
                setSelectedMembers(newValue.map(v => v.id));
              }}
              loading={loading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Specific Members"
                  placeholder="Choose members..."
                  helperText="Select individual family members who can view this"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <PersonIcon fontSize="small" sx={{ ml: 1, color: 'action.active' }} />
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    icon={<PersonIcon fontSize="small" />}
                    label={option.name}
                    {...getTagProps({ index })}
                    size="small"
                  />
                ))
              }
              sx={{ mb: 2 }}
            />

            {/* Select Groups */}
            <Autocomplete
              multiple
              options={familyGroups}
              getOptionLabel={(option) => option.name}
              value={familyGroups.filter(g => selectedGroups.includes(g.type))}
              onChange={(event, newValue) => {
                setSelectedGroups(newValue.map(v => v.type));
              }}
              loading={loading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Groups"
                  placeholder="Choose groups..."
                  helperText="Select family groups who can view this"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <GroupIcon fontSize="small" sx={{ ml: 1, color: 'action.active' }} />
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    icon={<GroupIcon fontSize="small" />}
                    label={option.name}
                    {...getTagProps({ index })}
                    size="small"
                  />
                ))
              }
            />

            {selectedMembers.length === 0 && selectedGroups.length === 0 && (
              <Typography variant="caption" color="warning.main" sx={{ mt: 1, display: 'block' }}>
                Please select at least one member or group for private content
              </Typography>
            )}
          </Box>
        )}
      </FormControl>
    </Paper>
  );
};

export default PrivacyControl;
